/* Terms of Service — CoinMBX (Megabyte Digispace Technologies Private Limited).

   Original draft prepared as a baseline document for an FIU-IND registered
   Indian cryptocurrency exchange. Should be reviewed by qualified counsel
   before being treated as the binding legal text.
*/
import { Link } from "react-router-dom";
import LegalLayout from "@/components/legal/LegalLayout";

const sections = [
  {
    id: "acceptance",
    heading: "Acceptance of these Terms",
    body: (
      <>
        <p>
          These Terms of Service (the &ldquo;<strong>Terms</strong>&rdquo;) form a legally binding
          agreement between you (&ldquo;<strong>you</strong>&rdquo;, &ldquo;<strong>User</strong>&rdquo;)
          and <strong>Megabyte Digispace Technologies Private Limited</strong>, the operator of the
          CoinMBX platform (&ldquo;<strong>CoinMBX</strong>&rdquo;, &ldquo;<strong>we</strong>&rdquo;,
          &ldquo;<strong>us</strong>&rdquo;), accessible at coinmbx.com and our mobile applications
          (collectively, the &ldquo;<strong>Platform</strong>&rdquo;).
        </p>
        <p>
          By creating an account, accessing or using the Platform, you confirm that you have read,
          understood and agree to be bound by these Terms, together with our Privacy Policy and
          any product-specific terms referenced herein. If you do not agree, you must not use the
          Platform.
        </p>
      </>
    ),
  },
  {
    id: "eligibility",
    heading: "Eligibility & Account Registration",
    body: (
      <>
        <p>To open and operate an account with CoinMBX, you must:</p>
        <ul>
          <li>be at least 18 years of age and competent to enter into a contract under Indian law;</li>
          <li>be a resident of India, unless we expressly extend services to your jurisdiction;</li>
          <li>not be a resident of, or located in, a sanctioned or restricted jurisdiction;</li>
          <li>not have been previously suspended or removed from the Platform.</li>
        </ul>
        <p>
          You are solely responsible for the accuracy of the information you provide. You must
          maintain a single primary account, keep your login credentials confidential, and
          immediately notify our helpdesk of any unauthorised access.
        </p>
      </>
    ),
  },
  {
    id: "kyc-aml",
    heading: "KYC, AML & Compliance",
    body: (
      <>
        <p>
          CoinMBX is registered as a Reporting Entity with the Financial Intelligence Unit – India
          (<strong>FIU-IND</strong>) under the Prevention of Money Laundering Act, 2002. To comply
          with applicable law, you authorise us to:
        </p>
        <ul>
          <li>collect and verify your identity, address and tax-residency documents;</li>
          <li>perform sanctions, PEP and adverse-media screening on you and your transactions;</li>
          <li>monitor account activity for suspicious or unusual patterns;</li>
          <li>file Suspicious Transaction Reports (STR) and Cash Transaction Reports (CTR) with the
              relevant authorities where required by law;</li>
          <li>request additional documents (source of funds, source of wealth, transaction
              purpose) at any time.</li>
        </ul>
        <p>
          We reserve the right to refuse, restrict, suspend or close any account, freeze any
          balance, or reverse any transaction where we determine, in our reasonable judgement, that
          continued service would breach applicable law or our internal compliance policies.
        </p>
      </>
    ),
  },
  {
    id: "services",
    heading: "Services Offered",
    body: (
      <>
        <p>
          The Platform may, subject to availability and your eligibility, provide:
        </p>
        <ul>
          <li>spot trading of digital assets against listed quote currencies;</li>
          <li>OTC (over-the-counter) block trading;</li>
          <li>fiat (INR) on-ramp and off-ramp facilitation through our banking partners;</li>
          <li>custody, deposit and withdrawal of digital assets supported by us.</li>
        </ul>
        <p>
          We may add, modify, suspend or remove any feature, asset listing or trading pair at our
          discretion, with or without prior notice. Service availability is not guaranteed; we may
          impose limits on order size, trade frequency, deposits, withdrawals and other operational
          parameters.
        </p>
      </>
    ),
  },
  {
    id: "fees",
    heading: "Fees, Taxes & Charges",
    body: (
      <>
        <p>
          Trading and service fees are published on our{" "}
          <Link to="/fees" className="text-cm-blue font-semibold hover:underline">
            fee schedule
          </Link>{" "}
          and may be changed at any time, with at least seven (7) days&rsquo; notice for material
          increases.
          Fees are debited from your CoinMBX wallet at the time of execution of the relevant
          transaction.
        </p>
        <p>
          You are solely responsible for determining and paying any taxes (including GST, TDS under
          Section 194S of the Income-tax Act, 1961, capital gains and any other levies) applicable
          to your transactions. CoinMBX may withhold and remit applicable taxes as required by law.
        </p>
      </>
    ),
  },
  {
    id: "deposits-withdrawals",
    heading: "Deposits, Withdrawals & Wallet Custody",
    body: (
      <>
        <p>
          Deposits in INR may be funded through bank transfers, UPI, or other rails offered by our
          banking partners. Cryptocurrency deposits must be made only to the address generated for
          you on the Platform. Sending unsupported assets, deposits via incorrect networks, or
          deposits originating from non-KYC sources may result in permanent loss of funds.
        </p>
        <p>
          Withdrawal requests are processed during operational windows and may be subject to
          additional verification. CoinMBX may delay or reject any withdrawal that triggers our
          risk or compliance controls. User assets are segregated from CoinMBX&rsquo;s operating
          accounts and the majority of digital assets are held in cold storage.
        </p>
      </>
    ),
  },
  {
    id: "risks",
    heading: "Risk Disclosure",
    body: (
      <>
        <p>
          <strong>
            Trading in digital assets is highly speculative and may result in the loss of your
            entire investment.
          </strong>{" "}
          Before using the Platform you should:
        </p>
        <ul>
          <li>understand that prices of digital assets are volatile and may change rapidly;</li>
          <li>be aware that digital assets are not legal tender, are not backed by any
              government, and are not covered by any deposit-insurance scheme;</li>
          <li>recognise that blockchain transactions, once confirmed, are typically
              irreversible;</li>
          <li>note that regulatory, tax and operational frameworks for digital assets are evolving
              and may impact the availability or value of your holdings.</li>
        </ul>
        <p>
          You confirm that you are trading using your own funds, with full understanding of these
          risks, and that you are not relying on CoinMBX for investment advice.
        </p>
      </>
    ),
  },
  {
    id: "prohibited",
    heading: "Prohibited Activities",
    body: (
      <>
        <p>You shall not, and shall not permit any third party to:</p>
        <ul>
          <li>use the Platform for any illegal, fraudulent or unauthorised purpose, including
              money laundering, terror financing, evasion of sanctions, or tax evasion;</li>
          <li>impersonate any person or misrepresent your identity, citizenship or affiliation;</li>
          <li>engage in market manipulation, wash trading, spoofing, layering, front-running or
              any other manipulative behaviour;</li>
          <li>attempt to interfere with, reverse-engineer, scrape or compromise the security of the
              Platform;</li>
          <li>create multiple accounts to circumvent rate limits, fee tiers, KYC requirements or
              jurisdictional restrictions;</li>
          <li>upload any content that is unlawful, defamatory, obscene, infringing, or harmful.</li>
        </ul>
        <p>
          Breach of this section may result in immediate account suspension, asset freezing and
          referral to law-enforcement authorities.
        </p>
      </>
    ),
  },
  {
    id: "ip",
    heading: "Intellectual Property",
    body: (
      <p>
        All content on the Platform — including software, designs, logos, trademarks, text, charts,
        APIs and documentation — is the property of CoinMBX or its licensors and is protected by
        applicable intellectual-property law. We grant you a limited, non-exclusive, non-
        transferable licence to use the Platform solely for your personal, non-commercial use, in
        accordance with these Terms.
      </p>
    ),
  },
  {
    id: "liability",
    heading: "Disclaimer & Limitation of Liability",
    body: (
      <>
        <p>
          The Platform is provided on an &ldquo;<strong>as-is</strong>&rdquo; and &ldquo;
          <strong>as-available</strong>&rdquo; basis. To the maximum extent permitted by law,
          CoinMBX disclaims all warranties, express or implied, including those of merchantability,
          fitness for a particular purpose, non-infringement, accuracy and uninterrupted operation.
        </p>
        <p>
          To the extent permitted by law, CoinMBX and its directors, officers, employees and
          affiliates shall not be liable for any indirect, incidental, special, consequential,
          exemplary or punitive damages, or for any loss of profits, revenue, data or goodwill,
          arising out of or in connection with your use of the Platform. Our aggregate liability,
          to the extent it cannot be excluded, is limited to the total fees paid by you to CoinMBX
          in the twelve (12) months preceding the relevant claim.
        </p>
      </>
    ),
  },
  {
    id: "indemnity",
    heading: "Indemnity",
    body: (
      <p>
        You agree to defend, indemnify and hold harmless CoinMBX, its affiliates and personnel from
        and against all claims, demands, losses, liabilities and expenses (including reasonable
        attorneys&rsquo; fees) arising out of or related to your use of the Platform, your breach
        of these Terms, your violation of any applicable law, or your violation of any third-party
        right.
      </p>
    ),
  },
  {
    id: "termination",
    heading: "Suspension & Termination",
    body: (
      <p>
        We may suspend, restrict or terminate your access to the Platform, with or without prior
        notice, where we reasonably believe you have breached these Terms, applicable law, or our
        compliance policies; where required by an order of a competent authority; or where
        continued service poses a security, reputational or operational risk. Upon termination, you
        remain liable for any amounts owed and we may retain records to comply with our regulatory
        obligations.
      </p>
    ),
  },
  {
    id: "dispute",
    heading: "Governing Law & Disputes",
    body: (
      <p>
        These Terms are governed by the laws of India. Subject to the dispute-resolution mechanism
        set out below, the courts at New Delhi shall have exclusive jurisdiction. Any dispute,
        controversy or claim arising out of or relating to these Terms shall first be attempted to
        be resolved through good-faith negotiation; failing which, by binding arbitration seated at
        New Delhi under the Arbitration and Conciliation Act, 1996, before a sole arbitrator
        appointed by mutual agreement, with proceedings conducted in English.
      </p>
    ),
  },
  {
    id: "changes",
    heading: "Changes to these Terms",
    body: (
      <p>
        We may update these Terms from time to time to reflect changes in our services, applicable
        law, or operational practices. Material changes will be notified to you by email and/or
        through the Platform at least seven (7) days prior to taking effect. Your continued use of
        the Platform after the effective date constitutes your acceptance of the updated Terms.
      </p>
    ),
  },
  {
    id: "contact",
    heading: "Contact Us",
    body: (
      <>
        <p>
          For any questions regarding these Terms, please reach out to our team:
        </p>
        <ul>
          <li><strong>Helpdesk:</strong> helpdesk@coinmbx.com</li>
          <li><strong>Phone:</strong> +91 87961 01200</li>
          <li>
            <strong>Address:</strong> Megabyte Digispace Technologies Private Limited, GD-ITL
            Northex Towers, Unit 201, 203 &amp; 204, 2nd Floor, Pitampura Flyover U Turn, Netaji
            Subhash Place, North West Delhi, Delhi 110034, India.
          </li>
        </ul>
      </>
    ),
  },
];

export default function Terms() {
  const intro = (
    <>
      In short: CoinMBX is operated by <strong>Megabyte Digispace Technologies Private
      Limited</strong>, a company registered in India and listed as a Reporting Entity with the
      Financial Intelligence Unit – India (FIU-IND). By using our Platform you agree to follow
      Indian law, complete KYC, trade only with your own money, and accept that crypto is
      volatile. We do our best to keep your funds safe, your data private, and the Platform
      reliable — but you remain responsible for your own trading decisions.
    </>
  );
  return (
    <LegalLayout
      testid="terms-page"
      title="Terms of Service"
      lastUpdated="30 April 2026"
      intro={intro}
      sections={sections}
    />
  );
}
